import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Button,
  Alert,
  LinearProgress,
} from '@mui/material';
import DeleteWorkItemPresetButton from './DeleteWorkItemPresetButton';
import AddWorkItemPreset from './AddWorkItemPreset';
import PresetDefaultParts from '../../components/Parts/PresetDefaultParts';
import { updateWorkItemPreset } from '../../actions/apiWorkItemPresets';

const WorkItemPresetTable = () => {
  const dispatch = useDispatch();

  const work_item_presets = useSelector(state => state.settings_refresh?.work_item_presets || []);
  const related_installations = useSelector(state => state.settings_dealerships?.related_installations || []);

  const [rowSelection, setRowSelection] = useState({});
  const [massDealerships, setMassDealerships] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [tableKey, setTableKey] = useState(0);

  const dealershipOptions = useMemo(() => {
    return related_installations.map(inst => ({
      value: inst.subdomain,
      label: inst.softname || inst.subdomain,
    }));
  }, [related_installations]);

  // Get unique estimate_type values for filter dropdown
  const estimateTypeOptions = useMemo(() => {
    const types = [...new Set(work_item_presets.map(item => item.estimate_type).filter(Boolean))];
    return types.sort();
  }, [work_item_presets]);

  const categoryOptions = ['cosmetic', 'mechanical', 'other']; // or pull dynamically if needed

  const columns = useMemo(() => [
    {
      id: 'edit',
      header: 'Edit',
      size: 80,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <AddWorkItemPreset workItem={row.original} />
        </Box>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 180,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 220,
    },
    {
      accessorKey: 'category',
      header: 'Work Item Type',
      size: 160,
      filterVariant: 'select',
      filterSelectOptions: categoryOptions.map(c => ({
        value: c,
        label: c.charAt(0).toUpperCase() + c.slice(1),
      })),
      Filter: ({ column }) => (
        <FormControl fullWidth size="small">
          <InputLabel>Work Item Type</InputLabel>
          <Select
            value={column.getFilterValue() || ''}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            label="Work Item Type"
          >
            <MenuItem value="">All</MenuItem>
            {categoryOptions.map(c => (
              <MenuItem key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      accessorKey: 'estimate_type',
      header: 'Type',
      size: 140,
      filterVariant: 'select',
      filterSelectOptions: estimateTypeOptions.map(t => ({ value: t, label: t })),
      Filter: ({ column }) => (
        <FormControl fullWidth size="small">
          <InputLabel>Type</InputLabel>
          <Select
            value={column.getFilterValue() || ''}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            label="Type"
          >
            <MenuItem value="">All</MenuItem>
            {estimateTypeOptions.map(t => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      accessorKey: 'parts_price',
      header: 'Parts Price',
      size: 130,
    },
    {
      accessorKey: 'labor_units',
      header: 'Labor Unit',
      size: 130,
    },
    {
      accessorKey: 'labor_price',
      header: 'Labor Price',
      size: 130,
    },
    {
      accessorKey: 'estimate_total',
      header: 'Total',
      size: 130,
    },
    {
      accessorKey: 'dealership',
      header: 'Dealership',
      size: 220,
      muiTableBodyCellProps: {
        sx: {
          padding: '8px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          maxWidth: 220,
          '&:hover': {
            overflow: 'visible',
            whiteSpace: 'normal',
            backgroundColor: '#f5f5f5',
            zIndex: 1,
            position: 'relative',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
      enableEditing: true,
      editVariant: 'select',
      filterVariant: 'select',
      filterSelectOptions: dealershipOptions,
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        const value = row.getValue(id);
        if (!value) return true; // blank = show in all (as you requested)
        return value.split(',').map(v => v.trim()).includes(filterValue);
      },
      Filter: ({ column }) => (
        <FormControl fullWidth size="small">
          <InputLabel>Dealership</InputLabel>
          <Select
            value={column.getFilterValue() || ''}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            label="Dealership"
          >
            <MenuItem value="">All</MenuItem>
            {dealershipOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      muiEditTextFieldProps: ({ row, table }) => {
        const dealershipArray = row.original.dealership && typeof row.original.dealership === 'string'
          ? row.original.dealership.split(',').filter(Boolean).map(d => d.trim())
          : [];

        return {
          select: true,
          multiple: true,
          value: dealershipArray,
          SelectProps: {
            renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
            MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
            multiple: true,
          },
          children: dealershipOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={dealershipArray.includes(opt.value)} />
              {opt.label}
            </MenuItem>
          )),
          onChange: async (event) => {
            const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
            const id = row.original.id;
            const data = { dealership: selectedValues.join(',') };

            try {
              await dispatch(updateWorkItemPreset(id, data));
              setTableKey(prev => prev + 1);
              table.setEditingCell(null);
            } catch (ex) {
              setError('Failed to update dealership');
              console.error(ex);
            }
          },
          onClick: (e) => e.stopPropagation(),
        };
      },
      Cell: ({ row }) => {
        const dealerships = row.original.dealership && typeof row.original.dealership === 'string'
          ? row.original.dealership.split(',').filter(Boolean)
          : [];
        const displayNames = dealerships
          .map(subdomain => {
            const opt = dealershipOptions.find(o => o.value === subdomain);
            return opt ? opt.label : subdomain;
          })
          .join(', ');
        return displayNames || 'All Dealerships (blank)';
      },
    },
    {
      id: 'delete',
      header: 'Delete',
      size: 80,
      Cell: ({ row }) => (
        <DeleteWorkItemPresetButton work_item_preset_id={row.original.id} />
      ),
    },
  ], [dealershipOptions, estimateTypeOptions, dispatch]);

  const data = useMemo(() => {
    return work_item_presets.map(p => ({
      ...p,
      dealership: p.dealership || '',
    }));
  }, [work_item_presets]);

  // Mass Override
  const handleMassOverride = async () => {
    const selected = Object.keys(rowSelection).map(i => data[parseInt(i)]);
    if (selected.length === 0 || massDealerships.length === 0) return;

    setUpdating(true);
    setError(null);

    try {
      const promises = selected.map(item =>
        dispatch(updateWorkItemPreset(item.id, { dealership: massDealerships.join(',') }))
      );
      await Promise.all(promises);
      setRowSelection({});
      setMassDealerships([]);
      setTableKey(prev => prev + 1);
    } catch (err) {
      setError('Mass override failed');
    } finally {
      setUpdating(false);
    }
  };

  // Mass Append
  const handleMassAppend = async () => {
    const selected = Object.keys(rowSelection).map(i => data[parseInt(i)]);
    if (selected.length === 0 || massDealerships.length === 0) return;

    setUpdating(true);
    setError(null);

    try {
      const promises = selected.map(item => {
        const current = (item.dealership || '').split(',').filter(Boolean).map(d => d.trim());
        const combined = [...new Set([...current, ...massDealerships])];
        return dispatch(updateWorkItemPreset(item.id, { dealership: combined.join(',') }));
      });
      await Promise.all(promises);
      setRowSelection({});
      setMassDealerships([]);
      setTableKey(prev => prev + 1);
    } catch (err) {
      setError('Mass append failed');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box>
      {/* Mass Update Bar */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#f8f9fa',
          borderRadius: 1,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <FormControl sx={{ minWidth: 280 }}>
          <InputLabel>Dealerships (Mass Update)</InputLabel>
          <Select
            multiple
            value={massDealerships}
            onChange={(e) => setMassDealerships(e.target.value)}
            label="Dealerships (Mass Update)"
            renderValue={(selected) => selected.join(', ')}
          >
            {dealershipOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={massDealerships.includes(opt.value)} />
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={handleMassOverride}
          disabled={Object.keys(rowSelection).length === 0 || massDealerships.length === 0 || updating}
        >
          Override Dealerships
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={handleMassAppend}
          disabled={Object.keys(rowSelection).length === 0 || massDealerships.length === 0 || updating}
        >
          Append Dealerships
        </Button>

        {updating && <LinearProgress sx={{ flex: 1, ml: 2 }} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <MaterialReactTable
        key={tableKey}
        columns={columns}
        data={data}
        enableRowSelection
        enableMultiRowSelection
        onRowSelectionChange={setRowSelection}
        state={{ rowSelection }}
        enableEditing
        editDisplayMode="cell"
        initialState={{
          density: 'compact',
          showColumnFilters: true,
        }}
        renderDetailPanel={({ row }) => (row.getIsExpanded() ? <PresetDefaultParts preset={row.original} /> : <div />)}
        renderTopToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <AddWorkItemPreset />
          </Box>
        )}
      />
    </Box>
  );
};

export default WorkItemPresetTable;